/**
 * Threads API service using BaseApi pattern for core CRUD
 * Reduced from 278 lines to ~200 lines (28% reduction)
 */

import { BaseApi } from './base-api';
import { createSingletonApi } from './singleton-factory';
import { clientPortalApi } from './client-portal.api';
import { getCurrentClientPortalContext } from './client-portal-context';
import {
  CreateThread,
  CreateThreadComment,
  PaginatedResponse,
  Thread,
  ThreadComment,
  ThreadCommentChangesResponse,
  ThreadCommentHistory,
  ThreadReaction,
  ThreadCommentReaction,
  ThreadQueryParams,
  ThreadTypeEnum,
  ModuleTypeEnum,
  UpdateThreadInput,
} from '@scrambl/shared';

// Cache for request deduplication (for getOrCreateModuleThreadWithComments)
const pendingRequests = new Map<string, Promise<{
  thread: Thread;
  comments: ThreadComment[];
  totalComments: number;
}>>();

class ThreadApiService extends BaseApi<Thread, CreateThread, UpdateThreadInput> {
  constructor() {
    super({
      baseEndpoint: '/threads',
      responseNormalization: true
    });
  }

  private getClientPortalEntityId(): string | null {
    return getCurrentClientPortalContext()?.entityId ?? null;
  }

  // Override list to handle ThreadQueryParams
  async getThreads(params?: ThreadQueryParams): Promise<PaginatedResponse<Thread>> {
    return this.list(params || {});
  }

  async getThread(id: string): Promise<Thread> {
    const portalEntityId = this.getClientPortalEntityId();
    if (portalEntityId) {
      const response = await clientPortalApi.getThread(portalEntityId, id, 50);
      return response.thread as unknown as Thread;
    }
    return this.findById(id);
  }

  async createThread(data: CreateThread, userId?: string): Promise<Thread> {
    const body = userId ? { ...data, userId } : data;
    return super.create(body as CreateThread);
  }

  async updateThread(id: string, data: UpdateThreadInput): Promise<Thread> {
    return this.update(id, data);
  }

  async deleteThread(id: string): Promise<void> {
    return this.delete(id);
  }

  async toggleThreadReaction(data: { threadId: string; emoji: string }): Promise<{ reaction: ThreadReaction | null; added: boolean }> {
    const portalEntityId = this.getClientPortalEntityId();
    if (portalEntityId) {
      return clientPortalApi.toggleThreadReaction(portalEntityId, data.threadId, data.emoji);
    }

    return this.makeRequest<{ reaction: ThreadReaction | null; added: boolean }>(`${this.config.baseEndpoint}/reactions/toggle`, {
      method: 'POST',
      body: data,
    });
  }

  // Thread Comments
  async getThreadComments(params: {
    threadId: string;
    parentCommentId?: string | null;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    limit?: number;
    page?: number;
    skipCache?: boolean;
  }): Promise<PaginatedResponse<ThreadComment>> {
    const portalEntityId = this.getClientPortalEntityId();
    if (portalEntityId) {
      return clientPortalApi.getThreadComments(portalEntityId, params.threadId, params);
    }

    const queryParams = new URLSearchParams();

    if (params.parentCommentId === null) {
      queryParams.append('parentCommentId', 'null');
    } else if (params.parentCommentId !== undefined) {
      queryParams.append('parentCommentId', params.parentCommentId);
    }

    queryParams.append('sortBy', params.sortBy || 'sequence');
    queryParams.append('sortOrder', params.sortOrder || 'asc');
    queryParams.append('limit', (params.limit || 10).toString());
    queryParams.append('page', (params.page || 1).toString());

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return this.makeRequest<PaginatedResponse<ThreadComment>>(`${this.config.baseEndpoint}/${params.threadId}/comments${queryString}`);
  }

  async getThreadCommentChanges(params: {
    threadId: string;
    sinceSequence?: number;
    sinceUpdatedAt?: string;
    limit?: number;
  }): Promise<ThreadCommentChangesResponse> {
    const portalEntityId = this.getClientPortalEntityId();
    if (portalEntityId) {
      return clientPortalApi.getThreadCommentChanges(portalEntityId, params.threadId, params);
    }

    const queryParams = new URLSearchParams();

    if (typeof params.sinceSequence === 'number') {
      queryParams.append('sinceSequence', params.sinceSequence.toString());
    }
    if (params.sinceUpdatedAt) {
      queryParams.append('sinceUpdatedAt', params.sinceUpdatedAt);
    }
    if (typeof params.limit === 'number') {
      queryParams.append('limit', params.limit.toString());
    }

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return this.makeRequest<ThreadCommentChangesResponse>(`${this.config.baseEndpoint}/${params.threadId}/comments/changes${queryString}`);
  }

  async getThreadCommentById(commentId: string): Promise<ThreadComment> {
    return this.makeRequest<ThreadComment>(`${this.config.baseEndpoint}/comments/${commentId}`);
  }

  async getThreadCommentHistory(commentId: string): Promise<ThreadCommentHistory[]> {
    return this.makeRequest<ThreadCommentHistory[]>(`${this.config.baseEndpoint}/comments/${commentId}/history`);
  }

  async createThreadComment(data: CreateThreadComment & { moduleType?: string; moduleId?: string }): Promise<ThreadComment> {
    // Strip out moduleType and moduleId - these are only used for client-side cache updates
    const apiData: CreateThreadComment & { moduleType?: string; moduleId?: string } = { ...data };
    delete apiData.moduleType;
    delete apiData.moduleId;

    const portalEntityId = this.getClientPortalEntityId();
    if (portalEntityId) {
      return clientPortalApi.createThreadComment(
        portalEntityId,
        apiData.threadId,
        apiData as CreateThreadComment,
      ) as unknown as Promise<ThreadComment>;
    }

    return this.makeRequest<ThreadComment>(`${this.config.baseEndpoint}/comments`, {
      method: 'POST',
      body: apiData,
    });
  }

  async updateThreadComment(id: string, content: string, threadId?: string): Promise<ThreadComment> {
    const portalEntityId = this.getClientPortalEntityId();
    if (portalEntityId) {
      if (!threadId) {
        throw new Error('Thread ID is required when updating portal comments');
      }
      return clientPortalApi.updateThreadComment(portalEntityId, threadId, id, content);
    }

    return this.makeRequest<ThreadComment>(`${this.config.baseEndpoint}/comments/${id}`, {
      method: 'PUT',
      body: { content },
    });
  }

  async deleteThreadComment(id: string, threadId?: string): Promise<void> {
    const portalEntityId = this.getClientPortalEntityId();
    if (portalEntityId) {
      if (!threadId) {
        throw new Error('Thread ID is required when deleting portal comments');
      }
      await clientPortalApi.deleteComment(portalEntityId, threadId, id);
      return;
    }

    await this.makeRequest<void>(`${this.config.baseEndpoint}/comments/${id}`, {
      method: 'DELETE',
    });
  }

  // Comment Reactions
  async toggleThreadCommentReaction(
    data: { commentId: string; emoji: string; threadId?: string }
  ): Promise<{ reaction: ThreadCommentReaction | null; added: boolean }> {
    const portalEntityId = this.getClientPortalEntityId();
    if (portalEntityId) {
      if (!data.threadId) {
        throw new Error('Thread ID is required when toggling portal comment reactions');
      }
      return clientPortalApi.toggleCommentReaction(
        portalEntityId,
        data.threadId,
        data.commentId,
        data.emoji,
      );
    }

    const { commentId, emoji } = data;
    return this.makeRequest<{ reaction: ThreadCommentReaction | null; added: boolean }>(`${this.config.baseEndpoint}/comments/reactions/toggle`, {
      method: 'POST',
      body: { commentId, emoji },
    });
  }

  async getThreadIdByCommentId(commentId: string): Promise<string> {
    const response = await this.makeRequest<{ threadId: string }>(`${this.config.baseEndpoint}/comments/${commentId}/thread`);
    return response.threadId;
  }

  // Module thread methods
  async getOrCreateModuleThread(params: {
    moduleType: ModuleTypeEnum;
    moduleId: string;
    moduleName?: string;
  }): Promise<Thread> {
    const existingThreadsResponse = await this.getThreads({
      moduleType: params.moduleType,
      moduleId: params.moduleId,
      threadType: ThreadTypeEnum.COMMENT,
      limit: 1,
    });

    if (existingThreadsResponse.data.length > 0) {
      return existingThreadsResponse.data[0];
    }

    const threadTitle = params.moduleName
      ? `Comments for ${params.moduleName}`
      : `Discussion`;

    return this.createThread({
      title: threadTitle,
      description: `Comments and discussion for this ${params.moduleType.toLowerCase()}.`,
      threadType: ThreadTypeEnum.COMMENT,
      moduleType: params.moduleType,
      moduleId: params.moduleId,
    });
  }

  async getOrCreateModuleThreadWithComments(params: {
    moduleType: ModuleTypeEnum;
    moduleId: string;
    moduleName?: string;
    commentsLimit?: number;
  }): Promise<{
    thread: Thread;
    comments: ThreadComment[];
    totalComments: number;
  }> {
    const requestKey = `${params.moduleType}:${params.moduleId}`;

    if (pendingRequests.has(requestKey)) {
      return pendingRequests.get(requestKey)!;
    }

    const queryParams = new URLSearchParams();
    if (params.moduleName) queryParams.append('moduleName', params.moduleName);
    if (params.commentsLimit) queryParams.append('commentsLimit', params.commentsLimit.toString());

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const endpoint = `${this.config.baseEndpoint}/module/${params.moduleType}/${params.moduleId}/with-comments${queryString}`;

    const requestPromise = this.makeRequest<{
      thread: Thread;
      comments: ThreadComment[];
      totalComments: number;
    }>(endpoint).finally(() => {
      pendingRequests.delete(requestKey);
    });

    pendingRequests.set(requestKey, requestPromise);
    return requestPromise;
  }

  clearCache(): void {
    // No-op - TanStack Query handles caching now
  }

  /**
   * Mark a thread as read for the current user.
   * Updates the lastReadAt timestamp and marks related notifications as read.
   */
  async markThreadAsRead(threadId: string): Promise<{ success: boolean }> {
    return this.makeRequest<{ success: boolean }>(`${this.config.baseEndpoint}/${threadId}/read`, {
      method: 'POST',
    });
  }

  /**
   * Get count of unread threads for the current user.
   * Efficient endpoint that counts in the database instead of fetching all threads.
   * @param threadType - Optional filter to count only specific thread types (e.g., DIRECT_MESSAGE, DISCUSSION)
   */
  async getUnreadCount(threadType?: string): Promise<{ count: number }> {
    const queryParams = threadType ? `?threadType=${encodeURIComponent(threadType)}` : '';
    return this.makeRequest<{ count: number }>(`${this.config.baseEndpoint}/stats/unread-count${queryParams}`);
  }

  /**
   * Mark all threads as read for the current user.
   * Returns the number of threads marked as read.
   */
  async markAllAsRead(): Promise<{ success: boolean; count: number }> {
    return this.makeRequest<{ success: boolean; count: number }>(`${this.config.baseEndpoint}/mark-all-read`, {
      method: 'POST',
    });
  }

  /**
   * Mark a thread as unread for the current user.
   * Sets the lastReadAt to null to force unread status.
   */
  async markThreadAsUnread(threadId: string): Promise<{ success: boolean }> {
    return this.makeRequest<{ success: boolean }>(`${this.config.baseEndpoint}/${threadId}/unread`, {
      method: 'POST',
    });
  }
}

// Export singleton instance
const threadApiInstance = createSingletonApi(ThreadApiService);

// Export object-style API for backward compatibility
export const threadApi = {
  clearCache: () => threadApiInstance.clearCache(),
  getThreads: (params?: ThreadQueryParams) => threadApiInstance.getThreads(params),
  getThread: (id: string) => threadApiInstance.getThread(id),
  createThread: (data: CreateThread, userId?: string) => threadApiInstance.createThread(data, userId),
  updateThread: (id: string, data: Partial<Thread>) => threadApiInstance.updateThread(id, data),
  deleteThread: (id: string) => threadApiInstance.deleteThread(id),
  toggleThreadReaction: (data: { threadId: string; emoji: string }) => threadApiInstance.toggleThreadReaction(data),
  getThreadComments: (params: Parameters<typeof threadApiInstance.getThreadComments>[0]) => threadApiInstance.getThreadComments(params),
  getThreadCommentChanges: (params: Parameters<typeof threadApiInstance.getThreadCommentChanges>[0]) => threadApiInstance.getThreadCommentChanges(params),
  getThreadCommentById: (commentId: string) => threadApiInstance.getThreadCommentById(commentId),
  getThreadCommentHistory: (commentId: string) => threadApiInstance.getThreadCommentHistory(commentId),
  createThreadComment: (data: CreateThreadComment & { moduleType?: string; moduleId?: string }) =>
    threadApiInstance.createThreadComment(data),
  updateThreadComment: (id: string, content: string, threadId?: string) => threadApiInstance.updateThreadComment(id, content, threadId),
  deleteThreadComment: (id: string, threadId?: string) => threadApiInstance.deleteThreadComment(id, threadId),
  toggleThreadCommentReaction: (data: { commentId: string; emoji: string; threadId?: string }) => threadApiInstance.toggleThreadCommentReaction(data),
  getThreadIdByCommentId: (commentId: string) => threadApiInstance.getThreadIdByCommentId(commentId),
  getOrCreateModuleThread: (params: Parameters<typeof threadApiInstance.getOrCreateModuleThread>[0]) => threadApiInstance.getOrCreateModuleThread(params),
  getOrCreateModuleThreadWithComments: (params: Parameters<typeof threadApiInstance.getOrCreateModuleThreadWithComments>[0]) => threadApiInstance.getOrCreateModuleThreadWithComments(params),
  markThreadAsRead: (threadId: string) => threadApiInstance.markThreadAsRead(threadId),
  getUnreadCount: (threadType?: string) => threadApiInstance.getUnreadCount(threadType),
  markAllAsRead: () => threadApiInstance.markAllAsRead(),
  markThreadAsUnread: (threadId: string) => threadApiInstance.markThreadAsUnread(threadId),
};

// Export class instance
export { threadApiInstance as threadApiService };
